"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Tile = exports.Z20Generator = void 0;
const Z20Entity_1 = require("./Z20Entity");
const Z20Graph_1 = require("./Z20Graph");
class Cell {
    constructor(x, y) {
        this.loc = { x, y };
        this.id = x * 1000 + y;
        this.dependents = [];
    }
    toString() {
        return `{${this.loc.x}, ${this.loc.y}}`;
    }
}
const bad5x5Layouts = [
    '11101\n00101\n11111\n10100\n10111',
    '10111\n10100\n11111\n00101\n11101'
];
class Z20Generator {
    constructor(config, setup) {
        this.nodes = [];
        this.config = this.standardizeConfig(config);
        this.setup = setup;
        this.random = setup.random;
        this.maze = [];
        this.cells = [];
        for (let x = 0; x < this.config.size.x; x++) {
            const arr = [];
            const cellArr = [];
            for (let y = 0; y < config.size.y; y++) {
                arr.push(Tile.Wall);
                cellArr.push(new Cell(x, y));
            }
            this.maze.push(arr);
            this.cells.push(cellArr);
        }
        this.generate();
        let str = "";
        let floorCount = 0;
        for (let y = 0; y < config.size.y; y++) {
            for (let x = 0; x < config.size.x; x++) {
                str += this.maze[x][y] === Tile.Wall ? "0" : "1";
                if (this.maze[x][y] === Tile.Floor)
                    floorCount++;
            }
            if (y != config.size.y - 1)
                str += '\n';
        }
        // console.log(str);
        // console.log(this.maze);
        // console.log('Floors', floorCount);
        // console.log(this.findDeadEnds());
        if (config.size.x === 5 && config.size.y === 5) {
            for (let layout of bad5x5Layouts) {
                if (str === layout)
                    throw new Error('Problematic layout');
            }
        }
        // console.log(this.graph);
        const ends = this.graph.terminalNodes();
        const branches = this.random.shuffledArray(this.findEndBranches());
        // console.log('branches', branches);
        if (branches.filter(x => x.length > 1).length < config.minDeadEnds) {
            throw new Error(`Too few ends. Required >= ${config.minDeadEnds}, had ${ends.length}.`);
        }
        // TODO: Move this into the setup.
        const winEntity = new Z20Entity_1.default(1000, { wins: true, sprite: 'trophy' });
        const placements = [];
        for (let i = 0; i < branches.length; i++) {
            const lockIdx = this.random.nextInt(1, branches[i].length);
            const keyIdx = this.random.nextInt(0, lockIdx);
            for (let j = 0; j < keyIdx; j++) {
                this.removeConnections(branches[i][j].thing);
            }
            placements.push([branches[i][keyIdx].thing, branches[i][lockIdx].thing]);
        }
        const shuffled = this.random.shuffledArray(placements);
        const shuffledTools = this.random.shuffledArray(setup.tools);
        let toolsUsed = 1;
        const requiredSlots = this.config.minLAKs + 2;
        const extraGroup = this.random.withChance(this.config.extraGroup);
        if (requiredSlots > shuffled.length || requiredSlots - 2 > shuffledTools.length) {
            throw new Error(`Can't fit all POIs. Needed ${requiredSlots}, had ${shuffled.length}`);
        }
        const slots = Math.min(this.config.maxLAKs + 2, shuffled.length);
        let offset = 0;
        let end = extraGroup ? slots - 2 : slots - 1;
        for (let i = offset; i < end; i++) {
            const blocked = shuffled[i][0];
            const blocker = shuffled[i][1];
            if (i === 0) {
                // Put trophy at the start
                this.nodes.push({ loc: blocked, entity: this.setup.trophy });
            }
            else {
                // Put a key at the end of other branches
                const keyEntity = setup.entities.filter(x => x.gives === shuffledTools[i - 1])[0];
                blocked.key = keyEntity;
                this.nodes.push({ loc: blocked, entity: keyEntity });
            }
            // If it's not the start of the unlock chain.
            if (i !== end - 1) {
                const lockEntity = setup.entities.filter(x => x.weakness === shuffledTools[i])[0];
                this.nodes.push({ loc: blocker, entity: lockEntity });
                blocker.lock = lockEntity;
            }
            ++offset;
            toolsUsed = i + 1;
        }
        if (extraGroup) {
            const tool = shuffledTools[offset];
            const group = shuffled[offset];
            const keyEntity = setup.entities.filter(x => x.gives === tool)[0];
            group[0].key = keyEntity;
            this.nodes.push({ loc: group[0], entity: keyEntity });
            const lockEntity = setup.entities.filter(x => x.weakness === tool)[0];
            group[1].lock = lockEntity;
            this.nodes.push({ loc: group[1], entity: lockEntity });
            ++offset;
        }
        this.nodes.push({ loc: shuffled[offset++][0], entity: this.setup.player });
        if (offset < shuffled.length && this.random.withChance(this.config.redHerring)) {
            const cell = shuffled[requiredSlots][0];
            const lockEntity = this.random.getRandom(setup.entities.filter(x => x.weakness !== undefined));
            cell.lock = lockEntity;
            this.nodes.push({ loc: cell, entity: lockEntity });
        }
        if (toolsUsed < config.minLAKs) {
            throw new Error(`Too few LAKs. Had ${toolsUsed}, required ${config.minLAKs}`);
        }
        this.paintGraph();
        this.maybeConnect();
        // console.log(this.graph);
    }
    standardizeConfig(config) {
        return {
            size: {
                x: Math.floor(config.size.x),
                y: Math.floor(config.size.y)
            },
            minDeadEnds: (config.minDeadEnds = Z20Generator.standardizeNumber(config.minDeadEnds, 0)) < 3 ? 3 : config.minDeadEnds,
            bonusConnection: Z20Generator.standardizeBooleanOrNumber(config.bonusConnectionChance, 0),
            oneOffConnection: Z20Generator.standardizeBooleanOrNumber(config.oneOffConnectionChance, 0),
            straightBias: config.straightBias || 0,
            maxLAKs: (config.maxLAKs = Z20Generator.standardizeNumber(config.maxLAKs, 0)) <= 0 ? Infinity : config.maxLAKs,
            minLAKs: (config.minLAKs = Z20Generator.standardizeNumber(config.minLAKs, 0)) <= 0 ? 0 : config.minLAKs,
            redHerring: Z20Generator.standardizeBooleanOrNumber(config.redHerringChance, 0),
            extraGroup: Z20Generator.standardizeBooleanOrNumber(config.extraGroupChance, 0),
        };
        // // config.maxSteps = (config.maxSteps = Z20Generator.standardizeNumber(config.maxSteps, 0)) <= 0 ? Infinity : config.maxSteps;
        // // config.minSteps = (config.minSteps = Z20Generator.standardizeNumber(config.minSteps, 0)) <= 0 ? 0 : config.minSteps;
    }
    static standardizeBooleanOrNumber(entry, defaultValue) {
        if (entry === undefined)
            return 0;
        else if (typeof entry == 'boolean')
            !!entry ? 1 : 0;
        else
            return this.standardizeNumber(entry, defaultValue);
    }
    static standardizeNumber(entry, defaultValue) {
        const numEntry = Number(entry);
        if (isNaN(numEntry))
            return defaultValue;
        return numEntry;
    }
    generate() {
        for (const pt of this.allRootCells()) {
            if (!this.isSettableAtPos(pt.loc))
                continue;
            this.growMaze(pt.loc.x, pt.loc.y);
        }
        for (let x = 0; x < this.config.size.x; x += 2) {
            for (let y = 0; y < this.config.size.y; y += 2) {
                if (!this.isSettableAt(x, y))
                    continue;
                this.growMaze(x, y);
            }
        }
        this.buildGraph();
    }
    buildGraph() {
        this.graph = new Z20Graph_1.Z20Graph();
        for (const cell of this.allRootCells()) {
            this.graph.ensureNode(cell);
        }
        for (const cell of this.allConnectionCells()) {
            if (!this.isPosType(cell.loc, Tile.Floor))
                continue;
            this.graph.ensureNode(cell);
            for (const neighbor of this.validCells1Away(cell.loc)) {
                if (this.isPosType(neighbor.loc, Tile.Floor)) {
                    this.graph.addConnection(cell, neighbor);
                }
            }
        }
    }
    addAllConnections(cell) {
        this.maze[cell.loc.x][cell.loc.y] = Tile.Floor;
        this.graph.ensureNode(cell);
        for (const neighbor of this.validCells1Away(cell.loc)) {
            if (this.isPosType(neighbor.loc, Tile.Floor)) {
                this.graph.addConnection(cell, neighbor);
            }
        }
    }
    removeConnections(cell) {
        this.maze[cell.loc.x][cell.loc.y] = Tile.Wall;
        this.graph.removeNode(cell);
    }
    isOneOff(set1, set2) {
        if (!set1 || !set2)
            return undefined;
        if (Math.abs(set1.size - set2.size) !== 1)
            return undefined;
        const s1a = [...set1.values()];
        const s2a = [...set2.values()];
        const diff1 = s1a.filter(x => !s2a.includes(x));
        if (diff1.length > 1)
            return undefined;
        const diff2 = s2a.filter(x => !s1a.includes(x));
        if (diff1.length + diff2.length !== 1)
            return undefined;
        return diff1.length > 0 ? diff1[0] : diff2[0];
    }
    maybeConnect() {
        for (const cell of this.allCells()) {
            if (!this.isPosType(cell.loc, Tile.Wall))
                continue;
            let blocker = undefined;
            let otherBlock = undefined;
            let diff = undefined;
            let mismatch = false;
            for (const neighbor of this.validCells1Away(cell.loc)) {
                if (!this.isPosType(neighbor.loc, Tile.Floor))
                    continue;
                if (blocker === undefined)
                    blocker = neighbor.blockers;
                else if (neighbor.blockers !== blocker && !(neighbor.blockers.size === 0 && blocker.size === 0)) {
                    if (otherBlock === undefined && (diff = this.isOneOff(blocker, neighbor.blockers)) !== undefined) {
                        otherBlock = neighbor.blockers;
                        continue;
                    }
                    else if (otherBlock === neighbor.blockers)
                        continue;
                    mismatch = true;
                    break;
                }
            }
            if (!mismatch && diff !== undefined && this.random.withChance(this.config.oneOffConnection)) {
                cell.blockers = blocker.size > otherBlock.size ? blocker : otherBlock;
                const gEntity = { loc: cell, entity: diff };
                this.nodes.push(gEntity);
                this.addAllConnections(cell);
            }
            if (!mismatch && otherBlock === undefined && this.random.withChance(this.config.bonusConnection)) {
                cell.blockers = blocker || new Set();
                this.addAllConnections(cell);
            }
        }
    }
    paintGraph() {
        const visited = new Set();
        const trail = [];
        const start = this.graph.getNode(this.nodes.filter(x => x.entity.player)[0].loc);
        this.paintAt(start, visited, new Set());
    }
    paintAt(curr, visited, blockers) {
        visited.add(curr.thing.id);
        curr.thing.blockers = blockers;
        if (curr.thing.lock) {
            blockers = new Set([...blockers, curr.thing.lock]);
        }
        for (let neighbor of curr.neighbors.filter(x => !visited.has(x.thing.id))) {
            this.paintAt(neighbor, visited, blockers);
        }
    }
    growMaze(sx, sy) {
        let cells = [];
        this.maze[sx][sy] = Tile.Floor;
        cells.push(new DirectedPoint(sx, sy, this.random.getRandomEnum(Direction)));
        let lastDir = Direction.Up;
        while (cells.length > 0) {
            // This is random to have a reasonable number of branches. If it's the last element, it's often just one long path.
            const dp = this.random.getRandom(cells);
            const filtered = this.cells2Away(dp).filter(x => this.isSettableAt(x.x, x.y) && this.maze[x.x][x.y] !== Tile.Floor);
            if (filtered.length > 0) {
                let next;
                if (this.random.withChance(this.config.straightBias) && filtered.filter(x => x.dir == lastDir).length > 0) {
                    next = filtered.filter(x => x.dir == lastDir)[0];
                }
                else {
                    next = this.random.getRandom(filtered);
                }
                this.setAt(next, Tile.Floor);
                this.setAt(dp.between(next), Tile.Floor);
                lastDir = next.dir;
                cells.push(next);
            }
            else {
                cells = cells.filter(x => x != dp);
            }
        }
    }
    setAt(dp, tile) {
        this.maze[dp.x][dp.y] = tile;
    }
    isSettableAtPos(pos) {
        return this.isPosType(pos, Tile.Wall);
    }
    isSettableAt(x, y) {
        return this.isType(x, y, Tile.Wall);
    }
    isPosOnGrid(pos) {
        return pos.x >= 0 && pos.y >= 0 && pos.x < this.config.size.x && pos.y < this.config.size.y;
    }
    isOnGrid(x, y) {
        return x >= 0 && y >= 0 && x < this.config.size.x && y < this.config.size.y;
    }
    cells2Away(cell) {
        return [
            new DirectedPoint(cell.x - 2, cell.y, Direction.Left),
            new DirectedPoint(cell.x + 2, cell.y, Direction.Right),
            new DirectedPoint(cell.x, cell.y - 2, Direction.Up),
            new DirectedPoint(cell.x, cell.y + 2, Direction.Down),
        ];
    }
    cells1Away(loc) {
        return [
            new DirectedPoint(loc.x - 1, loc.y, Direction.Left),
            new DirectedPoint(loc.x + 1, loc.y, Direction.Right),
            new DirectedPoint(loc.x, loc.y - 1, Direction.Up),
            new DirectedPoint(loc.x, loc.y + 1, Direction.Down),
        ];
    }
    validCells1Away(loc) {
        const cells = [];
        const checkAndAdd = (x, y) => {
            if (this.isOnGrid(x, y))
                cells.push(this.cells[x][y]);
        };
        checkAndAdd(loc.x - 1, loc.y);
        checkAndAdd(loc.x + 1, loc.y);
        checkAndAdd(loc.x, loc.y - 1);
        checkAndAdd(loc.x, loc.y + 1);
        return cells;
    }
    allRootCells() {
        const cells = [];
        for (let x = 0; x < this.config.size.x; x += 2) {
            for (let y = 0; y < this.config.size.y; y += 2) {
                cells.push(this.cells[x][y]);
            }
        }
        return cells;
    }
    allCells() {
        const cells = [];
        for (let x = 0; x < this.config.size.x; x++) {
            for (let y = 0; y < this.config.size.y; y++) {
                cells.push(this.cells[x][y]);
            }
        }
        return cells;
    }
    allCellsWithOffset(offset) {
        const cells = [];
        for (let x = offset.x; x < this.config.size.x; x += 2) {
            for (let y = offset.y; y < this.config.size.y; y += 2) {
                cells.push(this.cells[x][y]);
            }
        }
        return cells;
    }
    allConnectionCells() {
        const cells = [];
        cells.push(...this.allCellsWithOffset({ x: 1, y: 0 }));
        cells.push(...this.allCellsWithOffset({ x: 0, y: 1 }));
        return cells;
    }
    isPosType(pos, type) {
        return this.isPosOnGrid(pos) && this.maze[pos.x][pos.y] == type;
    }
    isType(x, y, type) {
        return this.isOnGrid(x, y) && this.maze[x][y] == type;
    }
    findEndBranches() {
        const terminals = this.graph.terminalNodes();
        const branches = [];
        for (const terminal of terminals) {
            let prev = terminal;
            let node = terminal.neighbors[0];
            const branch = [terminal];
            while (node.neighbors.length === 2) {
                let other = node.neighbors.filter(x => x != prev)[0];
                branch.push(node);
                prev = node;
                node = other;
            }
            branches.push(branch);
        }
        return branches;
    }
    findDeadEnds() {
        return this.graph.terminalNodes().map(x => x.thing.loc);
        // Old implementation. It works, but I've already done the work.
        // const ends = [];
        // for (const loc of this.allRootCells()) {
        //     if (this.cells1Away(loc).filter(x => this.isType(x.x, x.y, Tile.Floor)).length === 1) {
        //         ends.push(loc);
        //     }
        // }
        // return ends;
    }
}
exports.Z20Generator = Z20Generator;
var Tile;
(function (Tile) {
    Tile[Tile["Wall"] = 0] = "Wall";
    Tile[Tile["Floor"] = 1] = "Floor";
})(Tile || (exports.Tile = Tile = {}));
var Direction;
(function (Direction) {
    Direction[Direction["Up"] = 0] = "Up";
    Direction[Direction["Left"] = 1] = "Left";
    Direction[Direction["Right"] = 2] = "Right";
    Direction[Direction["Down"] = 3] = "Down";
})(Direction || (Direction = {}));
class DirectedPoint {
    constructor(x, y, dir) {
        this.x = x;
        this.y = y;
        this.dir = dir || Direction.Up;
    }
    between(other) {
        return new DirectedPoint((this.x + other.x) / 2, (this.y + other.y) / 2, other.dir);
    }
}
